/**
 * The price rail — a real positional scale for one district's registered deals.
 *
 * This is the centrepiece of the market card, and it is a range on purpose. The
 * product's whole position is that deed data supports a RANGE and never a single
 * property's value, so the range is the thing the eye lands on. A big headline
 * number would quietly claim the opposite.
 *
 * Reading it, low to high:
 *   the pale span   where most deals (≈80%) registered
 *   the solid span  where half of them registered — calibrated, and measured to
 *                   really hold about half on quarters the model never saw
 *   the upright     the typical price (median)
 *   the pin         this listing, when it has both a price and an area
 *
 * Direction: the rail stays low→high left→right in Arabic as well as English.
 * Prices are written in Western digits that read left to right, and mirroring
 * the scale under them makes the axis fight its own labels. Everything around
 * the rail flips with the page; the measured scale does not.
 */

import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/propertyUtils';
import type { MarketBand } from '../../types/marketData';

export interface MarketRailProps {
  band: MarketBand;
  /** Hides the pin and its label — used on district pages with no listing. */
  showListing?: boolean;
  className?: string;
}

export default function MarketRail({ band, showListing = true, className = '' }: MarketRailProps) {
  const { t } = useTranslation('marketData');

  const listing = showListing ? band.pricePerM2 : null;

  // Keep the pin on screen even when the asking price sits far outside the data.
  const rawLow = Math.min(band.lo80, listing ?? band.lo80);
  const rawHigh = Math.max(band.hi80, listing ?? band.hi80);
  const pad = Math.max((rawHigh - rawLow) * 0.08, 1);
  const min = rawLow - pad;
  const max = rawHigh + pad;
  const span = Math.max(max - min, 1);

  const pct = (value: number) => ((value - min) / span) * 100;

  const band80Left = pct(band.lo80);
  const band80Width = pct(band.hi80) - band80Left;
  const band50Left = pct(band.lo50);
  const band50Width = pct(band.hi50) - band50Left;
  const medianLeft = pct(band.median);
  const listingLeft = listing !== null ? pct(listing) : null;

  return (
    <div className={className} dir="ltr">
      {/* The pin sits above the track so it never hides the spans behind it. */}
      <div className="relative h-11">
        {listingLeft !== null && (
          <div
            className="absolute bottom-0 flex -translate-x-1/2 flex-col items-center"
            style={{ left: `${listingLeft}%` }}
          >
            <span className="whitespace-nowrap rounded-brand-sm border border-[var(--pv-border-soft)] bg-white px-2 py-1 text-[11px] font-semibold leading-none text-[var(--pv-color-primary)] shadow-brand-xs">
              {formatNumber(listing as number)}
            </span>
            <span className="mt-1 text-[10px] font-medium leading-none text-[var(--pv-color-text-muted)]">
              {t('band.yourPriceShort', { defaultValue: 'This listing' })}
            </span>
            <span
              aria-hidden="true"
              className="mt-1 h-2 w-px bg-[var(--pv-color-primary)]"
            />
          </div>
        )}
      </div>

      <div
        className="relative h-3 w-full rounded-full bg-[var(--pv-color-surface-sunken)]"
        role="img"
        aria-label={t('band.halfOfDeals', {
          low: formatNumber(band.lo50),
          high: formatNumber(band.hi50),
          defaultValue: 'Half of deals between {{low}} and {{high}}',
        })}
      >
        {/* where most deals landed */}
        <div
          className="absolute inset-y-0 rounded-full bg-brand-100"
          style={{ left: `${band80Left}%`, width: `${band80Width}%` }}
        />
        {/* where half of them landed */}
        <div
          className="absolute inset-y-0 rounded-full bg-brand-400/60"
          style={{ left: `${band50Left}%`, width: `${band50Width}%` }}
        />
        {/* the typical price */}
        <div
          className="absolute -top-1 -bottom-1 w-[2px] rounded-full bg-[var(--pv-color-primary)]"
          style={{ left: `${medianLeft}%` }}
        />
        {listingLeft !== null && (
          <div
            className="absolute -top-[5px] h-[22px] w-[3px] -translate-x-1/2 rounded-full bg-[var(--pv-color-primary)] ring-2 ring-white"
            style={{ left: `${listingLeft}%` }}
          />
        )}
      </div>

      {/* Only the two ends are labelled; the median is named in the card copy. */}
      <div className="mt-2 flex items-center justify-between text-[11px] tabular-nums text-[var(--pv-color-text-muted)]">
        <span>{formatNumber(band.lo80)}</span>
        <span className="font-semibold text-[var(--pv-color-text)]">{formatNumber(band.median)}</span>
        <span>{formatNumber(band.hi80)}</span>
      </div>
    </div>
  );
}
